import { type ReactNode, useState } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
  QueryCache
} from '@tanstack/react-query';

interface QueryProviderProps {
  children: ReactNode;
}

const defaultQueryOptions = {
  staleTime: 30_000,
  gcTime: 5 * 60_000,
  retry: (failureCount: number, error: unknown) => {
    if (error instanceof Error && 'status' in error) {
      const status = (error as { status?: number }).status ?? 0;
      if (status >= 400 && status < 500 && status !== 429) {
        return false;
      }
    }

    return failureCount < 3;
  },
  refetchOnWindowFocus: false,
  refetchOnReconnect: true
} as const;

function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error('Query error', { key: query.queryKey, error });
      }
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, context, mutation) => {
        console.error('Mutation error', { key: mutation.options.mutationKey, error, variables, context });
      }
    }),
    defaultOptions: {
      queries: defaultQueryOptions,
      mutations: {
        retry: 0,
        networkMode: 'online'
      }
    }
  });
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [client] = useState(() => createQueryClient());

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
